import { create } from 'zustand';
import { fetchFavorites, writeFavorites } from '@/services/firestore/favorites';

// Favorites are now Firestore-backed (users/{uid}/preferences/favorites) so
// they sync across devices. Local mirror in localStorage keeps reads instant
// and survives the auth-resume window on cold start; the store loads from
// Firestore once auth resolves and reconciles.

const CACHE_KEY = 'favorites';
const LEGACY_KEY = 'flutter.favorites';

function parseList(raw: string | null): string[] | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return null;
    return parsed.filter((v) => typeof v === 'string');
  } catch {
    return null;
  }
}

function readCache(): string[] {
  const current = parseList(localStorage.getItem(CACHE_KEY));
  if (current) return current;
  const legacy = parseList(localStorage.getItem(LEGACY_KEY));
  if (legacy) {
    localStorage.setItem(CACHE_KEY, JSON.stringify(legacy));
    return legacy;
  }
  return [];
}

function writeCache(list: string[]) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(list));
  } catch {
    // ignore quota
  }
}

type FavoritesState = {
  list: string[];
  uid: string | null;
  load: (uid: string) => Promise<void>;
  add: (name: string) => Promise<void>;
  remove: (name: string) => Promise<void>;
  clearForLogout: () => void;
};

export const useFavoritesStore = create<FavoritesState>((set, get) => ({
  list: readCache(),
  uid: null,

  load: async (uid) => {
    set({ uid });
    try {
      const remote = await fetchFavorites(uid);
      if (remote === null) {
        // Firestore has nothing yet — push the local cache up if any.
        const local = get().list;
        if (local.length > 0) await writeFavorites(uid, local);
        return;
      }
      set({ list: remote });
      writeCache(remote);
    } catch {
      // Network failure — keep the local cache and try again next load.
    }
  },

  add: async (name) => {
    if (!name) return;
    const { list, uid } = get();
    if (list.includes(name)) return;
    const next = [...list, name];
    set({ list: next });
    writeCache(next);
    if (uid) {
      try {
        await writeFavorites(uid, next);
      } catch {
        // Offline / failed write — cache is still updated, will sync on next
        // successful add/remove. (Could queue here if we want robust offline.)
      }
    }
  },

  remove: async (name) => {
    const { list, uid } = get();
    if (!list.includes(name)) return;
    const next = list.filter((v) => v !== name);
    set({ list: next });
    writeCache(next);
    if (uid) {
      try {
        await writeFavorites(uid, next);
      } catch {
        // see add()
      }
    }
  },

  clearForLogout: () => {
    set({ list: [], uid: null });
    try {
      localStorage.removeItem(CACHE_KEY);
    } catch {
      // ignore
    }
  },
}));
